import * as THREE from "three";

// Camera-facing ribbon of recent ball-center positions: thick at the head,
// tapering to nothing at the tail (THREE.Line can't do width, so this is a
// triangle strip whose side vectors face the camera each frame).
const MAX_POINTS = 96;
const MIN_STEP = 0.06; // world units between samples
const JUMP_RESET = 4; // a respawn/warp teleports the marble; don't streak
const HEAD_ALPHA = 0.85;

export interface TrailOptions {
  maxPoints?: number;
  minStep?: number;
  jumpReset?: number;
  headAlpha?: number;
  additive?: boolean;
  renderOrder?: number;
}

export interface Trail {
  obj: THREE.Mesh;
  setWidth(w: number): void;
  update(pos: THREE.Vector3, camera: THREE.Camera): void;
  /** Redraw retained history without sampling a new point (afterglow). */
  fade(camera: THREE.Camera, opacity: number): void;
  reset(): void;
  dispose(): void;
}

export function createTrail(
  color: THREE.ColorRepresentation = 0x7fd4ff,
  options: TrailOptions = {},
): Trail {
  const maxPoints = Math.max(2, Math.floor(options.maxPoints ?? MAX_POINTS));
  const minStep = options.minStep ?? MIN_STEP;
  const jumpReset = options.jumpReset ?? JUMP_RESET;
  const headAlpha = options.headAlpha ?? HEAD_ALPHA;
  const positions = new Float32Array(maxPoints * 2 * 3);
  const colors = new Float32Array(maxPoints * 2 * 4);
  const index: number[] = [];
  for (let i = 0; i < maxPoints - 1; i++) {
    const a = i * 2;
    index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 4));
  geometry.setIndex(index);
  geometry.setDrawRange(0, 0);
  const material = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: options.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "marble-trail";
  mesh.frustumCulled = false;
  mesh.renderOrder = options.renderOrder ?? 5;

  const tint = new THREE.Color(color);
  const history: THREE.Vector3[] = []; // newest first
  // `history[0]` follows the live marble every frame so the ribbon stays
  // attached. Sampling distance must therefore use a separate committed
  // anchor; comparing against the moving head prevents slow/kinematic motion
  // from ever accumulating MIN_STEP and stretches one old segment forever.
  const sampleAnchor = new THREE.Vector3();
  let hasSampleAnchor = false;
  let headWidth = 0.2;

  const camPos = new THREE.Vector3();
  const dir = new THREE.Vector3();
  const view = new THREE.Vector3();
  const side = new THREE.Vector3();
  const previousSide = new THREE.Vector3();

  function rewrite(camera: THREE.Camera, opacity = 1): void {
    camera.getWorldPosition(camPos);
    const last = history.length - 1;
    let hasPreviousSide = false;
    for (let i = 0; i < history.length; i++) {
      const p = history[i];
      // Central difference along the trail; degenerate at a single point.
      dir.copy(history[Math.max(0, i - 1)]).sub(history[Math.min(last, i + 1)]);
      view.copy(p).sub(camPos);
      side.crossVectors(dir, view);
      if (side.lengthSq() > 1e-10) side.normalize();
      else if (hasPreviousSide) side.copy(previousSide);
      else side.set(0, 1, 0);
      // Camera-facing strips can choose either side-vector sign. Keep it
      // continuous down the ribbon so sharp bends never twist the strip.
      if (hasPreviousSide && side.dot(previousSide) < 0) side.negate();
      previousSide.copy(side);
      hasPreviousSide = true;
      // Taper over the history that ACTUALLY exists. Using maxPoints here
      // made every short/new trail end at nearly full width, then snap off.
      const fade = last <= 0 ? 0 : 1 - i / last;
      side.multiplyScalar(headWidth * fade * 0.5);
      positions[i * 6] = p.x + side.x;
      positions[i * 6 + 1] = p.y + side.y;
      positions[i * 6 + 2] = p.z + side.z;
      positions[i * 6 + 3] = p.x - side.x;
      positions[i * 6 + 4] = p.y - side.y;
      positions[i * 6 + 5] = p.z - side.z;
      const alpha = headAlpha * Math.max(0, opacity) * fade * fade;
      for (const v of [i * 8, i * 8 + 4]) {
        colors[v] = tint.r;
        colors[v + 1] = tint.g;
        colors[v + 2] = tint.b;
        colors[v + 3] = alpha;
      }
    }
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.color.needsUpdate = true;
    geometry.setDrawRange(0, Math.max(0, (history.length - 1) * 6));
  }

  return {
    obj: mesh,
    setWidth(w: number): void {
      headWidth = w;
    },
    update(pos: THREE.Vector3, camera: THREE.Camera): void {
      const head = history[0];
      if (head && pos.distanceTo(head) > jumpReset) {
        history.length = 0;
        hasSampleAnchor = false;
      }
      if (!hasSampleAnchor) {
        sampleAnchor.copy(pos);
        hasSampleAnchor = true;
      }
      if (
        !history[0] || pos.distanceToSquared(sampleAnchor) >= minStep * minStep
      ) {
        history.unshift(pos.clone());
        sampleAnchor.copy(pos);
        if (history.length > maxPoints) history.length = maxPoints;
      } else {
        // Keep the visual head attached without moving the sampling anchor.
        history[0].copy(pos);
      }
      rewrite(camera);
    },
    fade(camera: THREE.Camera, opacity: number): void {
      if (opacity <= 0) {
        history.length = 0;
        hasSampleAnchor = false;
        geometry.setDrawRange(0, 0);
        return;
      }
      rewrite(camera, opacity);
    },
    reset(): void {
      history.length = 0;
      hasSampleAnchor = false;
      geometry.setDrawRange(0, 0);
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
    },
  };
}
